import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../environments/environment';
import { ConfigService } from '../core/config/config.service';
import { AuthStateService } from '../core/auth/auth-state.service';
import { MsalSessionService } from '../core/auth/msal-session.service';
import { OtpAuthService } from '../core/auth/otp-auth.service';
import { OtpSessionService } from '../core/auth/otp-session.service';
import { TokenStorageService } from '../core/auth/token-storage.service';
import { FIELD_LENGTHS } from '../shared/field-lengths';
import { OtpDialogComponent } from './otp-dialog/otp-dialog.component';
import { otpVerifyErrorKey } from './otp-verify-error';

const LOCALE_STORAGE_KEY = 'locale';
const DEFAULT_LOCALE = 'de';
const SUPPORTED_LOCALES = ['de', 'en'] as const;
type Locale = (typeof SUPPORTED_LOCALES)[number];

type LoginStep = 'email' | 'otp';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatMenuModule, ReactiveFormsModule, TranslatePipe, OtpDialogComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  private msalService = inject(MsalService);
  private msalSession = inject(MsalSessionService);
  private configService = inject(ConfigService);
  private otpAuthService = inject(OtpAuthService);
  private otpSession = inject(OtpSessionService);
  private tokenStorage = inject(TokenStorageService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  protected authState = inject(AuthStateService);

  protected readonly selectedLocale = signal<Locale>(this.resolveInitialLocale());
  protected readonly fieldLengths = FIELD_LENGTHS;
  protected readonly supportEmail = environment.supportEmail;

  protected readonly emailControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.email, Validators.maxLength(FIELD_LENGTHS.EMAIL_MAX)],
  });

  protected readonly step = signal<LoginStep>('email');
  protected readonly targetEmail = signal('');
  protected readonly requesting = signal(false);
  protected readonly verifying = signal(false);
  protected readonly requestError = signal(false);
  protected readonly otpError = signal<string | null>(null);

  constructor() {
    // Validators run on the raw control value, so leading/trailing whitespace (e.g. from a
    // pasted address) would otherwise keep a visually valid email stuck behind Validators.email.
    this.emailControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      const trimmed = value.trim();
      if (trimmed !== value) {
        this.emailControl.setValue(trimmed, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.translate.use(this.selectedLocale());
    if (this.msalSession.hasAccount() || this.tokenStorage.hasSession()) {
      this.router.navigate(['/']);
    }
  }

  ngOnDestroy(): void {
    this.authState.clearError();
  }

  loginWithMicrosoft(): void {
    this.msalService.loginRedirect({
      scopes: ['openid', 'profile', 'email', `api://${this.configService.azure.clientId}/access_as_users`],
      prompt: 'select_account',
    });
  }

  selectLocale(locale: Locale): void {
    this.selectedLocale.set(locale);
    this.translate.use(locale);
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }

  requestCode(): void {
    if (this.emailControl.invalid || this.requesting()) {
      return;
    }
    const email = this.emailControl.value.trim();
    this.requesting.set(true);
    this.requestError.set(false);
    this.otpAuthService.requestCode(email).subscribe({
      next: () => {
        this.requesting.set(false);
        this.targetEmail.set(email);
        this.otpError.set(null);
        this.step.set('otp');
      },
      error: () => {
        this.requesting.set(false);
        this.requestError.set(true);
      },
    });
  }

  onVerify(code: string): void {
    this.verifying.set(true);
    this.otpAuthService.verifyCode(this.targetEmail(), code).subscribe({
      next: (tokens) => {
        void this.otpSession.start(tokens).then((started) => {
          this.verifying.set(false);
          if (!started) {
            this.otpError.set(this.translate.instant('login.otp.errorSessionConflict'));
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        this.verifying.set(false);
        this.otpError.set(this.translate.instant(otpVerifyErrorKey(err, 'login.otp.errorRateLimited')));
      },
    });
  }

  onResend(): void {
    this.otpAuthService.requestCode(this.targetEmail()).subscribe({
      error: () => {
        this.otpError.set(this.translate.instant('login.requestError'));
      },
    });
  }

  onChangeEmail(): void {
    this.step.set('email');
    this.otpError.set(null);
  }

  private resolveInitialLocale(): Locale {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return (SUPPORTED_LOCALES as readonly string[]).includes(stored ?? '') ? (stored as Locale) : DEFAULT_LOCALE;
  }
}
